import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth, apiClient, useTheme } from "@/App";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  GraduationCap,
  ArrowLeft,
  Calendar,
  Clock,
  CheckCircle2,
  Circle,
  PlayCircle,
  Sparkles,
  FileText,
  ListChecks,
  Save,
  Sun,
  Moon,
  Settings,
  LogOut,
  CreditCard,
  Loader2,
  Share2,
  Copy,
  Check,
  Target,
} from "lucide-react";
import { format, parseISO, differenceInDays } from "date-fns";
import { motion } from "framer-motion";

const CollegeDetail = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { collegeId } = useParams();

  const [college, setCollege] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchingAI, setFetchingAI] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("not_started");
  
  // Share dialog state
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareMessage, setShareMessage] = useState("");
  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [category, setCategory] = useState("target");

  const fetchCollege = useCallback(async () => {
    try {
      const response = await apiClient.get(`/colleges/${collegeId}`);
      setCollege(response.data);
      setNotes(response.data.notes || "");
      setStatus(response.data.status || "not_started");
      setCategory(response.data.category || "target");
    } catch (error) {
      console.error("Error fetching college:", error);
      toast.error("College not found");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [collegeId, navigate]);

  useEffect(() => {
    fetchCollege();
  }, [fetchCollege]);

  const autoFetchTriggered = useRef(false);

  useEffect(() => {
    if (college && !college.ai_fetched && !autoFetchTriggered.current) {
      autoFetchTriggered.current = true;
      handleFetchAI();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [college]);

  const handleFetchAI = async () => {
    setFetchingAI(true);
    try {
      const response = await apiClient.post(`/colleges/${collegeId}/fetch-info`, {}, { timeout: 45000 });
      setCollege(response.data);
      toast.success("College information updated!");
    } catch (error) {
      console.error("Error fetching AI info:", error);
      if (error.code === 'ECONNABORTED') {
        toast.error("Request timed out. The AI is taking too long — please try again.");
      } else {
        toast.error("Failed to fetch college info. Please try again.");
      }
    } finally {
      setFetchingAI(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/colleges/${collegeId}`, {
        notes,
        status,
        category,
      });
      toast.success("Changes saved!");
      // Refresh college data to update local state
      await fetchCollege();
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleShare = async () => {
    if (status !== "submitted") {
      // Auto-update status to submitted before sharing
      setStatus("submitted");
      await handleSave();
    }
    
    setSharing(true);
    try {
      const response = await apiClient.post(`/colleges/${collegeId}/share`, {
        college_id: collegeId,
        message: shareMessage || null,
      });
      
      const fullShareUrl = `${window.location.origin}${response.data.share_url}`;
      setShareUrl(fullShareUrl);
      toast.success("Share link created!");
    } catch (error) {
      console.error("Error creating share:", error);
      if (error.response?.status === 400) {
        toast.error("Please mark your application as 'Submitted' before sharing");
      } else {
        toast.error("Failed to create share link");
      }
    } finally {
      setSharing(false);
    }
  };

  const copyShareUrl = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getStatusIcon = (s) => {
    switch (s) {
      case "submitted":
        return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      case "in_progress":
        return <PlayCircle className="w-5 h-5 text-violet-500" />;
      default:
        return <Circle className="w-5 h-5 text-zinc-400" />;
    }
  };

  const getDeadlineInfo = () => {
    if (!college?.deadline || college.deadline === "Rolling") {
      return { text: college?.deadline || "No deadline set", urgent: false };
    }
    try {
      const deadlineDate = parseISO(college.deadline);
      const daysLeft = differenceInDays(deadlineDate, new Date());
      const formatted = format(deadlineDate, "MMMM d, yyyy");

      if (daysLeft < 0) return { text: `Past deadline (${formatted})`, urgent: true };
      if (daysLeft === 0) return { text: `Due today! (${formatted})`, urgent: true };
      if (daysLeft <= 7) return { text: `${daysLeft} days left - ${formatted}`, urgent: true };
      if (daysLeft <= 30) return { text: `${daysLeft} days left - ${formatted}`, urgent: false };
      return { text: formatted, urgent: false };
    } catch {
      return { text: college.deadline, urgent: false };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!college) {
    return null;
  }

  const deadlineInfo = getDeadlineInfo();

  return (
    <div className="min-h-screen bg-background noise-bg pb-20 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                className="rounded-full"
                data-testid="back-btn"
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <GraduationCap className="w-8 h-8 text-primary" />
                <span className="font-bold text-xl font-outfit hidden sm:block">Admit List</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="rounded-full"
                data-testid="theme-toggle-btn"
                aria-label="Toggle theme"
              >
                {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="user-menu-btn">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback>{user?.name?.[0] || "U"}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <div className="flex items-center justify-start gap-2 p-2">
                    <div className="flex flex-col space-y-1 leading-none">
                      <p className="font-medium">{user?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/subscription")} data-testid="subscription-menu-item">
                    <CreditCard className="mr-2 h-4 w-4" />
                    Subscription
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="settings-menu-item">
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 md:px-8 py-8">
        {/* College Header */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight font-outfit mb-2">
                {college.name}
              </h1>
              <p className="text-muted-foreground">{college.application_type}</p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <Button
                variant="outline"
                onClick={handleFetchAI}
                disabled={fetchingAI}
                className="rounded-full"
                data-testid="fetch-ai-btn"
              >
                {fetchingAI ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    AI Researching... (15-20s)
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Refresh Info
                  </>
                )}
              </Button>

              <Button
                onClick={handleSave}
                disabled={saving}
                className="rounded-full"
                data-testid="save-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>

              {status === "submitted" && (
                <Button
                  variant="outline"
                  onClick={() => setShowShareDialog(true)}
                  className="rounded-full"
                  data-testid="share-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share Acceptance
                </Button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Share Dialog */}
        <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>🎉 Share Your Acceptance!</DialogTitle>
              <DialogDescription>
                Congratulations on getting accepted to {college?.name}! Create a shareable card to celebrate with friends and family.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {!shareUrl ? (
                <div className="space-y-4">
                  <div className="grid gap-2">
                    <Label htmlFor="share-message">Add a personal message (optional)</Label>
                    <Input
                      id="share-message"
                      placeholder="So excited to start my journey!"
                      value={shareMessage}
                      onChange={(e) => setShareMessage(e.target.value)}
                      data-testid="share-message-input"
                    />
                  </div>
                  <Button
                    onClick={handleShare}
                    disabled={sharing}
                    className="w-full rounded-full"
                    data-testid="create-share-btn"
                  >
                    {sharing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Share2 className="w-4 h-4 mr-2" />
                        Create Share Link
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                    <p className="text-sm text-emerald-700 dark:text-emerald-300 font-medium mb-2">
                      Your share link is ready!
                    </p>
                    <div className="flex items-center gap-2">
                      <Input
                        value={shareUrl}
                        readOnly
                        className="text-sm"
                        data-testid="share-url-input"
                      />
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={copyShareUrl}
                        data-testid="copy-share-url-btn"
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => window.open(shareUrl, "_blank")}
                    className="w-full rounded-full"
                    data-testid="preview-share-btn"
                  >
                    Preview Share Card
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Status & Category */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <Card>
                <CardContent className="p-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Application Status
                      </Label>
                      <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger className="w-full" data-testid="status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="not_started">
                            <div className="flex items-center gap-2">
                              <Circle className="w-4 h-4 text-zinc-400" />
                              Not Started
                            </div>
                          </SelectItem>
                          <SelectItem value="in_progress">
                            <div className="flex items-center gap-2">
                              <PlayCircle className="w-4 h-4 text-violet-500" />
                              In Progress
                            </div>
                          </SelectItem>
                          <SelectItem value="submitted">
                            <div className="flex items-center gap-2">
                              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                              Submitted
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-sm text-muted-foreground mb-2 block">School Category</Label>
                      <Select value={category} onValueChange={setCategory}>
                        <SelectTrigger className="w-full" data-testid="category-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="reach">
                            <div className="flex items-center gap-2">
                              <span className="w-3 h-3 rounded-full bg-rose-500" />
                              Reach School
                            </div>
                          </SelectItem>
                          <SelectItem value="target">
                            <div className="flex items-center gap-2">
                              <span className="w-3 h-3 rounded-full bg-amber-500" />
                              Target School
                            </div>
                          </SelectItem>
                          <SelectItem value="safety">
                            <div className="flex items-center gap-2">
                              <span className="w-3 h-3 rounded-full bg-emerald-500" />
                              Safety School
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Important Dates */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.15 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 font-outfit">
                    <Calendar className="w-5 h-5" />
                    Important Dates
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {college.ai_fetched ? (
                    <div className="space-y-6">
                      {/* Freshman Admission Dates */}
                      <div>
                        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                          Freshman Admission
                        </h4>
                        <div className="grid sm:grid-cols-2 gap-3">
                          {college.application_opens && (
                            <div className="flex justify-between items-center p-3 rounded-lg bg-secondary/50">
                              <span className="text-sm">Application Opens</span>
                              <span className="text-sm font-mono font-medium">{college.application_opens}</span>
                            </div>
                          )}
                          {college.early_decision_deadline && (
                            <div className="flex justify-between items-center p-3 rounded-lg bg-rose-50 dark:bg-rose-900/20">
                              <span className="text-sm">Early Decision</span>
                              <span className="text-sm font-mono font-medium text-rose-600 dark:text-rose-400">{college.early_decision_deadline}</span>
                            </div>
                          )}
                          {college.early_action_deadline && (
                            <div className="flex justify-between items-center p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20">
                              <span className="text-sm">Early Action</span>
                              <span className="text-sm font-mono font-medium text-amber-600 dark:text-amber-400">{college.early_action_deadline}</span>
                            </div>
                          )}
                          {college.priority_deadline && (
                            <div className="flex justify-between items-center p-3 rounded-lg bg-violet-50 dark:bg-violet-900/20">
                              <span className="text-sm">Priority Deadline</span>
                              <span className="text-sm font-mono font-medium text-violet-600 dark:text-violet-400">{college.priority_deadline}</span>
                            </div>
                          )}
                          {college.regular_deadline && (
                            <div className="flex justify-between items-center p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                              <span className="text-sm">Regular Decision</span>
                              <span className="text-sm font-mono font-medium text-blue-600 dark:text-blue-400">{college.regular_deadline}</span>
                            </div>
                          )}
                          {college.deadline && !college.regular_deadline && (
                            <div className="flex justify-between items-center p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                              <span className="text-sm">Deadline</span>
                              <span className="text-sm font-mono font-medium text-blue-600 dark:text-blue-400">{college.deadline}</span>
                            </div>
                          )}
                          {college.decisions_released && (
                            <div className="flex justify-between items-center p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                              <span className="text-sm">Decisions Released</span>
                              <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">{college.decisions_released}</span>
                            </div>
                          )}
                          {college.financial_aid_deadline && (
                            <div className="flex justify-between items-center p-3 rounded-lg bg-secondary/50">
                              <span className="text-sm">Financial Aid</span>
                              <span className="text-sm font-mono font-medium">{college.financial_aid_deadline}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Transfer Admission Dates */}
                      {(college.transfer_fall_deadline || college.transfer_spring_deadline) && (
                        <div>
                          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                            Transfer Admission
                          </h4>
                          <div className="grid sm:grid-cols-2 gap-3">
                            {college.transfer_fall_deadline && (
                              <div className="flex justify-between items-center p-3 rounded-lg bg-secondary/50">
                                <span className="text-sm">Fall Deadline</span>
                                <span className="text-sm font-mono font-medium">{college.transfer_fall_deadline}</span>
                              </div>
                            )}
                            {college.transfer_spring_deadline && (
                              <div className="flex justify-between items-center p-3 rounded-lg bg-secondary/50">
                                <span className="text-sm">Spring Deadline</span>
                                <span className="text-sm font-mono font-medium">{college.transfer_spring_deadline}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <p className="text-muted-foreground text-sm">
                        {fetchingAI ? "Fetching dates..." : "Click 'Refresh Info' to fetch important dates"}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Overview */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 font-outfit">
                    <FileText className="w-5 h-5" />
                    Overview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {college.overview ? (
                    <p className="text-foreground leading-relaxed">{college.overview}</p>
                  ) : (
                    <div className="text-center py-6">
                      <p className="text-muted-foreground text-sm">
                        {fetchingAI ? "Fetching overview..." : "No overview available. Click 'Refresh Info' above to fetch."}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Requirements */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 font-outfit">
                    <ListChecks className="w-5 h-5" />
                    Requirements
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {college.requirements && college.requirements.length > 0 ? (
                    <ul className="space-y-3">
                      {college.requirements.map((req, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-xs font-mono text-primary">{index + 1}</span>
                          </div>
                          <span className="text-foreground">{req}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-center py-6">
                      <p className="text-muted-foreground text-sm">
                        {fetchingAI ? "Fetching requirements..." : "No requirements listed. Click 'Refresh Info' above to fetch."}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Notes */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <Card className="sticky top-24">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 font-outfit">
                    <FileText className="w-5 h-5" />
                    My Notes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    placeholder="Add your notes here... (e.g., essay ideas, to-do items, contact info)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="min-h-[150px] resize-none"
                    data-testid="notes-textarea"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Notes are saved when you click "Save Changes"
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Cost & Stats Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.25 }}
            >
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-outfit flex items-center gap-2">
                    <span className="text-lg">💰</span>
                    Cost & Stats
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {college.tuition_out_state || college.tuition_in_state ? (
                    <>
                      {college.school_type === "Public" && college.tuition_in_state && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">In-State Tuition</span>
                          <span className="text-sm font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                            {college.tuition_in_state}
                          </span>
                        </div>
                      )}
                      {college.tuition_out_state && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            {college.school_type === "Public" ? "Out-of-State" : "Tuition"}
                          </span>
                          <span className="text-sm font-mono font-semibold">
                            {college.tuition_out_state}
                          </span>
                        </div>
                      )}
                      {college.room_and_board && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Room & Board</span>
                          <span className="text-sm font-mono">{college.room_and_board}</span>
                        </div>
                      )}
                      {college.ranking && (
                        <div className="flex items-center justify-between pt-2 border-t border-border">
                          <span className="text-sm text-muted-foreground">Ranking</span>
                          <span className="text-sm font-semibold">{college.ranking}</span>
                        </div>
                      )}
                      {college.acceptance_rate && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Acceptance Rate</span>
                          <span className="text-sm font-mono">{college.acceptance_rate}</span>
                        </div>
                      )}
                      {college.enrollment && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Enrollment</span>
                          <span className="text-sm">{college.enrollment}</span>
                        </div>
                      )}
                      {college.location && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Location</span>
                          <span className="text-sm">{college.location}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-sm text-muted-foreground">
                        {fetchingAI ? "Fetching cost info..." : "Cost data will appear after AI fetch"}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Quick Stats */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-outfit">Quick Info</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Status</span>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(status)}
                      <span className="text-sm font-medium capitalize">
                        {status.replace("_", " ")}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">AI Info</span>
                    <span className={`text-sm font-medium ${college.ai_fetched ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>
                      {college.ai_fetched ? "Fetched" : "Not fetched"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Application Type</span>
                    <span className="text-sm font-medium">{college.application_type}</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="mobile-nav p-2">
        <div className="flex items-center justify-around">
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2"
            onClick={() => navigate("/dashboard")}
            data-testid="mobile-nav-dashboard"
          >
            <GraduationCap className="w-5 h-5" />
            <span className="text-xs">Dashboard</span>
          </Button>
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2"
            onClick={() => navigate("/calendar")}
            data-testid="mobile-nav-calendar"
          >
            <Calendar className="w-5 h-5" />
            <span className="text-xs">Calendar</span>
          </Button>
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2"
            onClick={() => navigate("/categories")}
            data-testid="mobile-nav-categories"
          >
            <Target className="w-5 h-5" />
            <span className="text-xs">Categories</span>
          </Button>
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2"
            onClick={() => navigate("/settings")}
            data-testid="mobile-nav-settings"
          >
            <Settings className="w-5 h-5" />
            <span className="text-xs">Settings</span>
          </Button>
        </div>
      </nav>
    </div>
  );
};

export default CollegeDetail;
